import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiClientError, type Chat, type Message, type Page, type PageRequest } from '../api/types.js'
import { IconChats } from '../icons.js'
import { Alert } from '../ui/Alert.js'
import { Attachment } from '../ui/Attachment.js'

/**
 * Узкий срез клиента: экрану нужны только беседы инстанса и сообщения
 * выбранной беседы (барьер 2 — клиент API есть граница процесса для
 * экранов админки, его двойник в тестах разрешён, см. Instances.tsx).
 * Экран только читает — прав на запись не требует и в пропсах их не ждёт.
 */
export interface ChatsClient {
  chats(instanceId: string, page?: PageRequest): Promise<Page<Chat>>
  messages(chatId: string, page?: PageRequest): Promise<Page<Message>>
  /** Содержимое вложения. Показывается по blob:-ссылке (FR-ADM-045). */
  media(id: string): Promise<Blob>
}

export interface ChatsProps {
  client: ChatsClient
  instanceId: string
}

/** Сообщение по умолчанию для отказов не от сервера (например, обрыв сети). */
const GENERIC_FAILURE = 'Не удалось выполнить действие. Проверьте соединение и попробуйте ещё раз.'

const MESSAGE_STATUS_LABELS: Record<string, string> = {
  queued: 'В очереди',
  sent: 'Отправлено',
  delivered: 'Доставлено',
  read: 'Прочитано',
  failed: 'Ошибка',
}

function messageStatusLabel(status: string): string {
  return MESSAGE_STATUS_LABELS[status] ?? status
}

/**
 * Заголовок беседы сейчас всегда пуст — приём входящих его не заполняет
 * (inbound.ts передаёт null в chats.ensure). Без подстраховки список
 * показывал бы пустые строки вместо того, чем реально можно опознать
 * собеседника, поэтому запасной вариант — идентификатор провайдера.
 */
function chatLabel(chat: Chat): string {
  return chat.title !== null && chat.title !== '' ? chat.title : chat.providerId
}

export function Chats(props: ChatsProps) {
  const { client, instanceId } = props

  const [chats, setChats] = useState<Chat[] | null>(null)
  const [chatsError, setChatsError] = useState<string | null>(null)

  const [selectedChatId, setSelectedChatId] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[] | null>(null)
  // Курсор идёт в ту же сторону, что и выдача сообщений (от старых к новым):
  // null означает «страниц больше нет», а не «ещё не запрашивали».
  const [messagesCursor, setMessagesCursor] = useState<string | null>(null)
  const [messagesError, setMessagesError] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)

  // Какой беседе на самом деле принадлежит ответ, который вот-вот придёт.
  // При быстром переключении бесед запросы могут разрешиться не в том
  // порядке, в котором были отправлены (сеть не гарантирует очередь);
  // без сверки с этим ref устаревший ответ по прежней беседе перезаписал
  // бы уже показанную переписку новой.
  const requestedChatIdRef = useRef<string | null>(null)

  /*
   * Ссылка на скачивание устойчива между отрисовками: вложение качается
   * по этой функции, и новая функция на каждую отрисовку заставляла бы
   * качать содержимое заново при любом изменении экрана.
   */
  const скачатьВложение = useCallback((id: string) => client.media(id), [client])

  const loadChats = useCallback(async () => {
    try {
      const page = await client.chats(instanceId)
      setChats(page.items)
    } catch (cause) {
      setChatsError(cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE)
    }
  }, [client, instanceId])

  useEffect(() => {
    void loadChats()
  }, [loadChats])

  async function selectChat(chatId: string) {
    // Смена собеседника сбрасывает предыдущую переписку целиком: список
    // сообщений и курсор относятся к конкретной беседе, а не накапливаются
    // между беседами.
    requestedChatIdRef.current = chatId
    setSelectedChatId(chatId)
    setMessages(null)
    setMessagesError(null)
    setMessagesCursor(null)
    try {
      const page = await client.messages(chatId, {})
      // Пока ответ летел, могли успеть выбрать другую беседу — тогда этот
      // ответ уже устарел, и применять его к экрану нельзя (иначе он
      // затрёт переписку беседы, выбранной позже).
      if (requestedChatIdRef.current !== chatId) return
      setMessages(page.items)
      setMessagesCursor(page.nextCursor)
    } catch (cause) {
      if (requestedChatIdRef.current !== chatId) return
      setMessagesError(cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE)
    }
  }

  async function loadMoreMessages() {
    if (selectedChatId === null || messagesCursor === null || loadingMore) return
    const chatId = selectedChatId
    setLoadingMore(true)
    setMessagesError(null)
    try {
      const page = await client.messages(chatId, { cursor: messagesCursor })
      // Та же проверка, что в selectChat: пока подгружалась следующая
      // страница, могли переключиться на другую беседу — её ответ не должен
      // дописаться к уже показанной переписке новой беседы.
      if (requestedChatIdRef.current !== chatId) return
      // Сообщения приходят от старых к новым, курсор продолжает в ту же
      // сторону — следующая страница дописывается в конец, а не в начало.
      setMessages((current) => [...(current ?? []), ...page.items])
      setMessagesCursor(page.nextCursor)
    } catch (cause) {
      if (requestedChatIdRef.current === chatId) {
        // Уже загруженные сообщения остаются на экране — неудачная подгрузка
        // следующей страницы не должна стирать то, что человек уже прочитал.
        setMessagesError(cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE)
      }
    } finally {
      setLoadingMore(false)
    }
  }

  return (
    <section aria-label="Переписка">
      {chatsError !== null && <Alert onDismiss={() => setChatsError(null)}>{chatsError}</Alert>}

      <div className="card" style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
        <section aria-label="Беседы" style={{ flex: '1 1 16rem', minWidth: '14rem' }}>
          <h2>Беседы</h2>
          {chats === null ? (
            <p>Загрузка бесед…</p>
          ) : chats.length === 0 ? (
            <div className="empty-state">
              <IconChats />
              <p>Бесед пока нет — они появятся сами, как только на номер придёт первое сообщение.</p>
            </div>
          ) : (
            <ul className="entity-list">
              {chats.map((chat) => (
                <li key={chat.id}>
                  <button
                    type="button"
                    className="btn"
                    style={{ width: '100%', justifyContent: 'flex-start' }}
                    aria-current={selectedChatId === chat.id ? 'true' : undefined}
                    onClick={() => void selectChat(chat.id)}
                  >
                    {chatLabel(chat)}
                    {chat.isGroup && <span title="Групповая беседа"> · группа</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section aria-label="Сообщения" style={{ flex: '2 1 24rem', minWidth: '18rem' }}>
          <h2>Сообщения</h2>
          {selectedChatId === null ? (
            <p>Выберите беседу, чтобы посмотреть сообщения.</p>
          ) : messages === null ? (
            messagesError !== null ? (
              <Alert onDismiss={() => setMessagesError(null)}>{messagesError}</Alert>
            ) : (
              <p>Загрузка сообщений…</p>
            )
          ) : messages.length === 0 ? (
            <p>Сообщений пока нет — отправьте сообщение этому собеседнику, чтобы начать переписку.</p>
          ) : (
            <>
              {messagesError !== null && <Alert onDismiss={() => setMessagesError(null)}>{messagesError}</Alert>}
              <ul className="entity-list">
                {messages.map((message) => (
                  <li
                    key={message.id}
                    data-direction={message.direction}
                    className={`message message--${message.direction}`}
                  >
                    {message.body !== null && message.body !== '' && <p>{message.body}</p>}
                    {message.media !== null && (
                      <Attachment media={message.media} load={скачатьВложение} />
                    )}
                    <p>{messageStatusLabel(message.status)}</p>
                  </li>
                ))}
              </ul>
            </>
          )}

          {messages !== null && messages.length > 0 && messagesCursor !== null && (
            <button type="button" className="btn" disabled={loadingMore} onClick={() => void loadMoreMessages()}>
              Показать ещё
            </button>
          )}
        </section>
      </div>
    </section>
  )
}
